import cloneDeep from 'lodash.clonedeep';
import {
  ProductFilters, ProductFilterRequest,
  FilterValue, ProductSorting, ProductFilter,
} from '~/restAPI/data-contracts';
import type { TFiltersPresets } from '~/types/filters';

export function useProductsFiltersDialog(
  productFilters: Ref<ProductFilters | undefined>,
  baseCategory: Ref<IBreadCrumbsItem | undefined>,
  filterItem: Ref<ProductFilter | undefined>,
  presets: Ref<TFiltersPresets | undefined>,
  sorting: Ref<string | undefined>,
) {
  const { getFilters, getProductsFilterInfo, cancelGetFilters } = useCatalog();

  const filtersModel = ref<ProductFilter[]>();
  const sortingModel = ref<ProductSorting['code']>();
  const itemsCount = ref<number>();
  const shouldAvailableFiltersLoad = ref(true);

  const filtersRequest = computed<ProductFilterRequest>(() => {
    const filters = (filtersModel.value ?? []).reduce((result, filter) => {
      if (isBooleanFilter(filter)) {
        if (filter.value) {
        // eslint-disable-next-line no-param-reassign
          result[filter.code] = filter.value;
        }
      } else if (isPriceFilter(filter)) {
        if (filter.selectedLower || filter.selectedUpper) {
          // eslint-disable-next-line no-param-reassign
          result[filter.code] = {
            lower: filter.selectedLower,
            upper: filter.selectedUpper,
          };
        }
      } else if (filter.selectedValues?.length) {
        // eslint-disable-next-line no-param-reassign
        result[filter.code] = filter.selectedValues?.map((item) => item.id);
      }

      return result;
    }, {} as Record<string, FilterValue>);

    Object.keys(filters).forEach((key) => !filters[key] && delete filters[key]);

    return {
      baseCategory: baseCategory.value?.id,
      filters,
      presets: presets.value,
      sorting: sortingModel.value !== 'NEW' ? sortingModel.value : undefined,
    };
  });

  const {
    data: availableFiltersData,
    pending: getAvailableFiltersPending,
    execute: getAvailableFilters,
  } = useLazyAsyncData(async () => {
    const res = await getFilters({
      ...filtersRequest.value,
      pageLength: 0,
      sorting: undefined,
    });

    if (res?.filters) {
      shouldAvailableFiltersLoad.value = false;
      filtersModel.value = cloneDeep(res.filters);
    }

    itemsCount.value = res?.itemsCount;

    return res;
  }, { immediate: false });

  getAvailableFiltersPending.value = false;

  const {
    data: filterInfoData,
    pending: getFilterInfoPending,
    execute: getFilterInfo,
  } = useLazyAsyncData(() => getProductsFilterInfo(
    filtersRequest.value,
    { code: filterItem.value?.code },
  ), { immediate: false });

  function generateItemsCount(count?: number) {
    if (typeof count === 'number') {
      itemsCount.value = count;
      return;
    }

    itemsCount.value = productFilters.value?.itemsCount;
  }

  function generateSorting() {
    sortingModel.value = sorting.value ?? productFilters.value?.sorting?.find((i) => i.isSelected)?.code;
  }

  function generateFilters(model?: ProductFilter[]) {
    filtersModel.value = model ? cloneDeep(model) : cloneDeep(productFilters.value?.filters);
  }

  return {
    filtersModel,
    sortingModel,
    filtersRequest,
    getAvailableFiltersPending,
    itemsCount,
    generateSorting,
    generateFilters,
    availableFiltersData,
    getAvailableFilters,
    shouldAvailableFiltersLoad,
    generateItemsCount,
    filterInfoData,
    getFilterInfoPending,
    getFilterInfo,
    cancelGetFilters,
  };
}
